// pages/BookingPage.jsx
// Part 3 of CALENDAR-WHATSAPP-SCOPE.md — PUBLIC, unauthenticated client-facing
// booking page at brokerconnect.co.uk/book/:slug. Same public-page pattern as
// pages/AgreementPage.jsx (no PrivateRoute wrapper, no sidebar). Calls
// routes/booking.js's public API only. White-labelled via broker_companies.branding
// (logo/colours), not Aurinko's own hosted widget branding — see CALENDAR-WHATSAPP-
// SCOPE.md's Part 3 note on why.
//
// Redesigned 18 Aug 2026 (Tim's request) to a Calendly-style layout: left info panel
// (logo/company/adviser/duration/bio), month calendar in the middle, time slots on
// the right — same three-pane pattern Calendly uses. No API/data-shape changes, just
// how the same GET /config + GET /availability responses are grouped by calendar day
// and rendered. All times are shown in the visitor's own browser time zone (plain
// `Date` + `toLocaleString`, no manual offset math) — same as Calendly's default.
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, Phone, Check, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const AVAILABILITY_WINDOW_DAYS = 30;
const WEEKDAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function pad2(n) { return String(n).padStart(2, '0'); }
function dateKey(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function sameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }

function formatTime(iso) {
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${pad2(m)}${ampm}`;
}
function formatDayHeading(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
function formatMonthYear(d) {
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function BookingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Item D (auto-allocation) - an intake-pool link can carry a Back Book client id,
  // e.g. /book/:slug?backbookClientId=<id>, so the API can assign them to whichever
  // adviser they actually land on once the booking is made - only if unassigned.
  const backbookClientId = searchParams.get('backbookClientId') || undefined;
  // 4 Sep 2026 - same shape as backbookClientId above, now carried on every link type
  // (services/bookingLinks.js), not just the intake-pool one: lets the API tie this
  // booking to the specific property the renewal alert was actually about, so a
  // multi-property client booking about one property doesn't silence alerts for a
  // second, unrelated one (see 041_bookings_backbook_property.sql).
  const backbookPropertyId = searchParams.get('backbookPropertyId') || undefined;
  // Item A (personal per-adviser links, query-param mechanism) - a link can carry
  // ?adviser=<booking_page_advisers.id> to pre-select a specific adviser on a page
  // that has more than one, skipping the picker entirely for a client who already
  // belongs to that adviser. Validated against the real config.advisers list once it
  // loads (below) - an unmatched/stale id is ignored and the picker shows as normal.
  const adviserParam = searchParams.get('adviser') || '';
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selectedAdviser, setSelectedAdviser] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // dateKey string
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    fetch(`${API}/booking/${slug}/config`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((cfg) => {
        setConfig(cfg);
        // Item A - a page with exactly one adviser attached has only one possible
        // outcome (personal per-adviser links, and any 'named' page an owner has only
        // added a single person to). Not gated on a special flag - purely "is there
        // more than one choice to make" - so pre-select it and the picker UI below
        // never renders.
        if (cfg.mode === 'named' && Array.isArray(cfg.advisers) && cfg.advisers.length === 1) {
          setSelectedAdviser(cfg.advisers[0].id);
        } else if (cfg.mode === 'named' && adviserParam && Array.isArray(cfg.advisers) && cfg.advisers.some(a => a.id === adviserParam)) {
          setSelectedAdviser(adviserParam);
        }
      })
      .catch(() => setLoadError("This booking page isn't available right now."));
  }, [slug]);

  const canLoadSlots = config && (config.mode === 'simple_rotation' || selectedAdviser);

  useEffect(() => {
    if (!canLoadSlots) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    const now = new Date();
    const end = new Date(now.getTime() + AVAILABILITY_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const params = new URLSearchParams({ startTime: now.toISOString(), endTime: end.toISOString() });
    if (config.mode === 'named') params.set('adviserId', selectedAdviser);
    fetch(`${API}/booking/${slug}/availability?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setSlots(d.items || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [canLoadSlots, config, selectedAdviser, slug]);

  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const key = dateKey(new Date(s.start));
      (map[key] = map[key] || []).push(s);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.start) - new Date(b.start)));
    return map;
  }, [slots]);

  const availableDateKeys = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  // Auto-select the first day that actually has availability, and jump the calendar
  // to that month — a client shouldn't land on a page with an empty grid when the
  // real earliest slot is a couple of weeks out.
  useEffect(() => {
    if (!availableDateKeys.length) { setSelectedDate(null); return; }
    setSelectedDate((prev) => (prev && slotsByDate[prev]) ? prev : availableDateKeys[0]);
    setViewDate((prev) => {
      const first = new Date(`${availableDateKeys[0]}T00:00:00`);
      return sameMonth(prev, first) ? prev : first;
    });
  }, [availableDateKeys, slotsByDate]);

  const durationMinutes = useMemo(() => {
    if (!slots.length) return null;
    return Math.round((new Date(slots[0].end) - new Date(slots[0].start)) / 60000);
  }, [slots]);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const maxNavDate = useMemo(() => new Date(today.getTime() + (AVAILABILITY_WINDOW_DAYS - 1) * 24 * 60 * 60 * 1000), [today]);
  const canGoPrev = !sameMonth(viewDate, today) && (viewDate.getFullYear() > today.getFullYear() || viewDate.getMonth() > today.getMonth());
  const canGoNext = !sameMonth(viewDate, maxNavDate) && (viewDate.getFullYear() < maxNavDate.getFullYear() || viewDate.getMonth() < maxNavDate.getMonth());

  const selectedAdviserName = config?.advisers?.find((a) => a.id === selectedAdviser)?.name;

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true); setBookingError('');
    try {
      const res = await fetch(`${API}/booking/${slug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adviserId: config.mode === 'named' ? selectedAdviser : undefined,
          start: selectedSlot.start,
          end: selectedSlot.end,
          name: form.name,
          email: form.email,
          phone: form.phone,
          backbookClientId,
          backbookPropertyId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setConfirmed(data.booking);
      else setBookingError(data.error || 'Failed to book — please try another slot.');
    } catch {
      setBookingError('Failed to book — please try another slot.');
    }
    setSubmitting(false);
  };

  const branding = config?.branding || {};
  const primaryColor = branding.primary_color || '#10b981';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (loadError) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 420, padding: '40px 32px', textAlign: 'center' }}>
          <p style={{ color: '#991b1b', fontSize: 14, margin: 0 }}>{loadError}</p>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 420, padding: '40px 32px', textAlign: 'center' }}>
          <p style={{ color: '#a0aec0', fontSize: 14, margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 440, padding: '48px 40px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Check size={28} color="#10b981" strokeWidth={3} />
          </div>
          {branding.logo_url && <img src={branding.logo_url} alt={config.companyName} style={{ maxHeight: 36, marginBottom: 18 }} />}
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0e1b2c', margin: '0 0 8px' }}>You're booked in</h1>
          <p style={{ color: '#4b5a6e', fontSize: 15, margin: '0 0 4px', fontWeight: 600 }}>
            {formatDayHeading(new Date(confirmed.start_time))}
          </p>
          <p style={{ color: '#4b5a6e', fontSize: 14, margin: 0 }}>
            {formatTime(confirmed.start_time)} – {formatTime(confirmed.end_time)} ({tz})
          </p>
          <p style={{ color: '#718096', fontSize: 13, marginTop: 20, paddingTop: 20, borderTop: '1px solid #EDF2F7' }}>
            A confirmation has been sent to your email.
          </p>
        </div>
      </div>
    );
  }

  const monthGrid = buildMonthGrid(viewDate);
  const selectedDaySlots = selectedDate ? (slotsByDate[selectedDate] || []) : [];

  // ── Step 3: details form — a deliberately standalone single-column view, not the
  // calendar/adviser panel with the form bolted on. Earlier version kept the whole
  // left info panel (logo, adviser picker, bio) rendered alongside the form here,
  // which just duplicated everything the visitor already picked. Tim flagged this
  // live (18 Aug 2026) — fixed by giving this step its own compact header instead.
  if (selectedSlot) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 440, width: '100%' }}>
          <div style={formPanel}>
            <button type="button" onClick={() => setSelectedSlot(null)} style={backLink}>
              <ArrowLeft size={14} /> Back
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              {branding.logo_url
                ? <img src={branding.logo_url} alt={config.companyName} style={{ height: 28, objectFit: 'contain' }} />
                : <div style={{ fontSize: 14, fontWeight: 700, color: '#0e1b2c' }}>{config.companyName}</div>}
            </div>

            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0e1b2c', margin: '0 0 10px', lineHeight: 1.3 }}>
              {selectedAdviserName ? `Meeting with ${selectedAdviserName}` : 'Book a meeting'}
            </h1>
            <div style={{ fontSize: 13, color: '#4b5a6e', marginBottom: 2, fontWeight: 600 }}>{formatDayHeading(new Date(selectedSlot.start))}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#718096', marginBottom: 24 }}>
              <Clock size={14} color="#a0aec0" />
              {formatTime(selectedSlot.start)} – {formatTime(selectedSlot.end)} ({tz})
            </div>

            <form onSubmit={handleBook}>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={input} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={input} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Phone (optional)</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={input} />
              </div>
              {bookingError && <div style={{ color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{bookingError}</div>}
              <button type="submit" disabled={submitting} style={confirmBtn(primaryColor, submitting)}>
                {submitting ? 'Booking...' : 'Confirm Booking'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={{ ...card, maxWidth: 860, width: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>

          {/* ── Left info panel ─────────────────────────────────────────────── */}
          <div style={leftPanel}>
            {branding.logo_url
              ? <img src={branding.logo_url} alt={config.companyName} style={{ maxHeight: 40, marginBottom: 18, objectFit: 'contain' }} />
              : <div style={{ fontSize: 17, fontWeight: 700, color: '#0e1b2c', marginBottom: 18 }}>{config.companyName}</div>}

            <div style={{ fontSize: 13, color: '#718096', fontWeight: 600, marginBottom: 4 }}>{config.companyName}</div>
            <h1 style={{ fontSize: 19, fontWeight: 700, color: '#0e1b2c', margin: '0 0 16px', lineHeight: 1.3 }}>
              {selectedAdviserName ? `Meeting with ${selectedAdviserName}` : 'Book a meeting'}
            </h1>

            {durationMinutes && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5a6e', marginBottom: 10 }}>
                <Clock size={15} color="#a0aec0" />
                {durationMinutes} min
              </div>
            )}
            {branding.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5a6e', marginBottom: 10 }}>
                <Phone size={15} color="#a0aec0" />
                {branding.phone}
              </div>
            )}

            {config.mode === 'named' && config.advisers.length > 1 && !(adviserParam && config.advisers.some(a => a.id === adviserParam)) && (
              <div style={{ marginTop: 20 }}>
                <label style={label}>Choose an adviser</label>
                {config.advisers.length <= 4 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                    {config.advisers.map((a) => (
                      <button key={a.id} type="button" onClick={() => setSelectedAdviser(a.id)}
                        style={pillBtn(selectedAdviser === a.id, primaryColor)}>
                        {a.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <select value={selectedAdviser} onChange={(e) => setSelectedAdviser(e.target.value)} style={{ ...input, marginTop: 6 }}>
                    <option value="">Select...</option>
                    {config.advisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                )}
              </div>
            )}

            {branding.company_bio && (
              <p style={{ fontSize: 12, color: '#a0aec0', lineHeight: 1.6, marginTop: 20, paddingTop: 18, borderTop: '1px solid #EDF2F7' }}>
                {branding.company_bio.length > 220 ? `${branding.company_bio.slice(0, 220)}…` : branding.company_bio}
              </p>
            )}
          </div>

          {/* ── Right side: calendar + times ─────────────────────────────────── */}
          {!canLoadSlots ? (
            <div style={{ ...calendarPanel, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', fontSize: 13 }}>
              Choose an adviser to see availability
            </div>
          ) : (
            <>
              <div style={calendarPanel}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#0e1b2c' }}>{formatMonthYear(viewDate)}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" disabled={!canGoPrev} onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} style={navBtn(canGoPrev)}>
                      <ChevronLeft size={16} />
                    </button>
                    <button type="button" disabled={!canGoNext} onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} style={navBtn(canGoNext)}>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                  {WEEKDAY_HEADERS.map((w, i) => (
                    <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#a0aec0', padding: '4px 0' }}>{w}</div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                  {monthGrid.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const key = dateKey(d);
                    const available = !!slotsByDate[key];
                    const isSelected = key === selectedDate;
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={!available}
                        onClick={() => setSelectedDate(key)}
                        style={dayCell(available, isSelected, primaryColor)}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11, color: '#a0aec0', marginTop: 14, marginBottom: 0 }}>Times shown in your time zone ({tz})</p>
              </div>

              <div style={timesPanel}>
                {selectedDate && <div style={{ fontSize: 13, fontWeight: 700, color: '#0e1b2c', marginBottom: 12 }}>{formatDayHeading(new Date(`${selectedDate}T00:00:00`))}</div>}
                {slotsLoading && <p style={{ color: '#a0aec0', fontSize: 13 }}>Loading availability...</p>}
                {!slotsLoading && !availableDateKeys.length && (
                  <p style={{ color: '#a0aec0', fontSize: 13 }}>No availability found in the next {AVAILABILITY_WINDOW_DAYS} days.</p>
                )}
                {!slotsLoading && selectedDaySlots.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                    {selectedDaySlots.map((s, i) => (
                      <button key={i} type="button" onClick={() => setSelectedSlot(s)} style={timeBtn(primaryColor)}>
                        {formatTime(s.start)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const pageWrap = { minHeight: '100vh', background: '#F7FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' };
const card = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(14,27,44,0.08)', overflow: 'hidden' };
// maxWidth: '100%' on each keeps these from forcing horizontal scroll on narrow
// phone widths, since they wrap onto their own rows via the parent's flexWrap.
const leftPanel = { width: 240, maxWidth: '100%', flex: '0 0 240px', padding: '32px 28px', borderRight: '1px solid #EDF2F7', boxSizing: 'border-box' };
const calendarPanel = { width: 340, maxWidth: '100%', flex: '0 0 340px', padding: '32px 28px', borderRight: '1px solid #EDF2F7', boxSizing: 'border-box' };
const timesPanel = { width: 200, maxWidth: '100%', flex: '1 1 200px', minWidth: 200, padding: '32px 24px', boxSizing: 'border-box' };
const formPanel = { flex: '1 1 auto', padding: '32px 32px', boxSizing: 'border-box', width: '100%' };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5a6e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.03em' };
const input = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 14, color: '#0e1b2c', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const backLink = { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#718096', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 20 };

const pillBtn = (active, color) => ({
  padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: 'left', cursor: 'pointer',
  border: `1px solid ${active ? color : '#E2E8F0'}`,
  background: active ? color : '#fff',
  color: active ? '#fff' : '#0e1b2c',
});

const navBtn = (enabled) => ({
  width: 26, height: 26, borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: enabled ? '#4b5a6e' : '#e2e8f0', cursor: enabled ? 'pointer' : 'default',
});

// branding.primary_color could theoretically be set to something that isn't a plain
// 6-digit hex (rgb(...), a name, etc.) — guard the alpha-suffix trick below so an
// unexpected format degrades to a safe default tint instead of rendering broken CSS.
function lightTint(hex) {
  return /^#[0-9a-f]{6}$/i.test(hex || '') ? `${hex}1A` : '#ECFDF5';
}

const dayCell = (available, selected, color) => ({
  aspectRatio: '1', width: '100%', borderRadius: 8, fontSize: 13, fontWeight: 600,
  border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: available ? 'pointer' : 'default',
  background: selected ? color : (available ? lightTint(color) : 'transparent'),
  color: selected ? '#fff' : (available ? '#0e1b2c' : '#cbd5e0'),
});

const timeBtn = (color) => ({
  padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: `1px solid ${color}`, background: '#fff', color, textAlign: 'center',
});

const confirmBtn = (color, loading) => ({
  padding: '10px 24px', background: loading ? '#a0aec0' : color, color: '#fff', border: 'none',
  borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', width: '100%',
});
